import React, { useState } from "react";
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei'
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { viewModel , leftArrow, rightArrow } from "../assets";
import { characters, furniture, animEnv, Slide } from "../constants/modelingConstants";

interface SlideShowProps {
    slides: Slide[]
}

function Model({ modelName, ...props }) {
    const modelPath = process.env.PUBLIC_URL + '/models/' + modelName + '.glb';
    const gltf = useLoader(GLTFLoader, modelPath);
    return <primitive object={gltf.scene} {...props} />;
}


const CanvasWrapper = ({ model, image }) => {
    const [is3DViewToggled, setIs3DViewToggled] = useState(false);

    const toggle3dView = () => {
        setIs3DViewToggled(!is3DViewToggled);
    };

    return (
        <div className="canvas-wrapper" title={model}>
            <img className={`canvas-wrapper__icon canvas-wrapper__icon${is3DViewToggled ? '--open' : ''}`} src={viewModel.src} alt={viewModel.alt} onClick={toggle3dView}/>
            {is3DViewToggled ? (
                <Canvas shadows camera={{ position: [-4, 3, 8], fov: 45 }}>
                    <ambientLight intensity={0.5} />
                    <spotLight position={[10, 25, 10]} angle={0.3} />
                    <directionalLight intensity={3} castShadow />
                    <Model modelName={model} scale={1.1} />
                    <OrbitControls enableZoom={false} />
                </Canvas>
            ) : (
            <img className="canvas-wrapper__image" src={image.src} alt={image.alt} />
            )}
        </div>
    );
};

const SlideShowItem = ({ slide }) => {
    if (slide.type === 'model') {
        return (<CanvasWrapper model={slide.content} image={slide.image} />)
    }
    if (slide.type === 'animation') {
        return (<video src={slide.content.src} controls >
            <source src={slide.content.src} type="video/mp4" />
            Your browser does not support the video tag.
        </video>)
    }
    if (slide.type === 'image') {
        return (<img src={slide.content.src} alt={slide.content.alt} />)
    }
    return null;
};

const SlideShow : React.FC<SlideShowProps> = ({ slides }) => {
    const [index, setIndex] = useState(0);

    const nextSlide = () => {
        setIndex((prevIndex) => {
            if (prevIndex >= ((slides.length + 2))) return 0;
            return prevIndex + 1;
        });
    };

    const prevSlide = () => {
        setIndex((prevIndex) => {
            if (prevIndex <= 0) return prevIndex;
            return prevIndex - 1;
        });
    };

    return (
        <div className="slideshow">
            <button onClick={prevSlide} className="slide-arrow left-arrow">
                <img src={leftArrow.src} alt={leftArrow.alt} />
            </button>
            <div className="slideshow-container">
                <div className="model-container" style={{ transform: `translateX(-${index * 300}px)` }}>
                    {slides.map((slide, idx) => (
                        <SlideShowItem key={idx}  slide={slide}/>
                    ))}
                </div>
            </div>
            <button onClick={nextSlide} className="slide-arrow right-arrow">
                <img src={rightArrow.src} alt={rightArrow.alt} />
            </button>
        </div>

    );
};

const Modeling = () => {
    return (
        <section className="modeling" id="modeling">
            <h1 className="underline-title">3D Modeling - Using Blender</h1>
            <p> I have developed a range of 3D models for academic projects, including my bachelor's degree, and as personal hobbies, blending technical skill and creative exploration in 3D modeling.</p>
            <div className="modeling__info">
                <p>Switch between the immersive 3D view and the detailed 2D view by clicking the icon</p>
                <img src={viewModel.src} alt={viewModel.alt} />
                <p> .</p>
                <p>Interact with the models in the 3D view by clicking and dragging on them.</p>
            </div>

            <h2>Furniture</h2>
            <SlideShow slides={ furniture } />
            <h2>Characters</h2>
            <SlideShow slides={ characters } />
            <h2>Rendered Animations and Environments</h2>
            <SlideShow slides={ animEnv } />
        </section>
    );
};

export default Modeling;